import React from 'react';
import { X, Mail, Building, Shield } from 'lucide-react';

interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: {
        name?: string;
        user_name?: string;
        username?: string;
        email: string;
        profile_image?: string;
        company?: {
            name: string;
        };
    } | null;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user }) => {
    if (!isOpen || !user) return null;

    const userDisplayName = user.name || user.user_name || user.username || user.email || 'User';
    const userInitial = userDisplayName.charAt(0).toUpperCase();

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 border-4 border-black">
                {/* Header */}
                <div className="relative h-32 bg-gradient-to-r from-[#2C9944] to-[#30A84B] flex items-center justify-center">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors text-white"
                    >
                        <X size={20} />
                    </button>
                    <div className="absolute -bottom-12 flex flex-col items-center">
                        <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-[#2C9944] flex items-center justify-center text-white text-3xl font-bold">
                            {userInitial}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="pt-16 pb-8 px-8 flex flex-col items-center gap-6">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-[#101928]">{userDisplayName}</h2>

                        <p className="text-[#667185] font-medium">User Profile</p>
                    </div>

                    <div className="w-full space-y-4 pt-4 border-t border-[#f0f2f5]">
                        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-[#e4e7ec] shadow-sm">
                                <Mail className="w-5 h-5 text-[#475367]" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[11px] font-semibold text-[#98A2B3] uppercase tracking-wider">Email Address</span>
                                <span className="text-[14px] font-medium text-[#101928]">{user.email}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-[#e4e7ec] shadow-sm">
                                <Building className="w-5 h-5 text-[#475367]" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[11px] font-semibold text-[#98A2B3] uppercase tracking-wider">Company</span>
                                <span className="text-[14px] font-medium text-[#101928]">{user.company?.name || 'Not Available'}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-[#e4e7ec] shadow-sm">
                                <Shield className="w-5 h-5 text-[#475367]" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[11px] font-semibold text-[#98A2B3] uppercase tracking-wider">Permissions</span>
                                <span className="text-[14px] font-medium text-[#101928]">Full Access</span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full mt-4 py-3 bg-[#101928] hover:bg-black text-white rounded-xl font-bold transition-all active:scale-[0.98] shadow-lg"
                    >
                        Close Profile
                    </button>
                </div>
            </div>
        </div>
    );
};
